import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const BillSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    // Basic Settings
    bill_prefix: 'BS',
    bill_start_number: 1,
    include_year_in_number: true,
    estimation_prefix: 'ES',
    estimation_start_number: 1,
    bill_title: 'tax_invoice',
    bill_title_custom: '',
    tax_method: 'inclusive', // inclusive (default) or exclusive
    show_gst_breakdown: true,
    show_savings: true,
    footer_text: 'Thank you for your purchase!',
    terms_text: 'Goods once sold will not be exchanged or returned.',
    print_logo: true,
    paper_size: '80mm',
    // A4 Invoice Settings
    enable_a4_invoice: false, // Enable A4 GST Invoice PDF
    default_print_format: 'thermal', // thermal or a4
    // Price Edit Permission
    allow_price_edit: false, // Allow editing selling price during billing
    price_edit_permission: 'owner_only', // owner_only or owner_and_cashiers
    // Header Info
    show_company_name: true,
    show_address: true,
    show_gst_number: true,
    show_contact_number: true,
    // Bill Info
    show_bill_number: true,
    show_date_time: true,
    // Customer Info
    show_customer_name: true,
    show_customer_mobile: true,
    // Item Columns
    show_serial_number: true,
    show_hsn_code: true,
    show_mrp: true,
    show_tax_column: true,
    show_discount_column: true,
    // Payment Info
    show_payment_mode: true,
    show_change_amount: true,
    // Custom Notes
    bill_notes: '',
    // Hardware Settings
    printer_type: 'browser', // browser, bluetooth, usb
    bluetooth_device_name: '',
    usb_printer_path: '',
    // Previous Date Billing
    allow_previous_date_bill: false,
    previous_date_permission: 'owner_only',
    // QR Code Settings
    show_qr_code: false,
    qr_code_type: 'url',
    qr_code_url: '',
    // Default Bill Mode & Direct Print
    default_bill_mode: 'cash',
    direct_print_on_payment: false,
    // Quick Add Custom Items (Non-Stock)
    allow_quick_add_items: false,
    quick_add_permission: 'owner_only', // owner_only or owner_and_cashiers
    // Estimation Behavior
    estimation_with_delivery: false, // Affects stock, no tax
    // A4 Invoice Detail Controls
    a4_show_company_name: true,
    a4_show_address: true,
    a4_show_email: true,
    a4_show_phone: true,
    a4_show_gst_number: true,
    a4_show_tax_summary: true,
    a4_show_mrp: true,
    a4_show_product_image: false,
    // Letterpad / No-Header Mode
    a4_no_header: false,
    a4_top_margin: 0,
    // Quick Pick (Primary Products Drawer)
    show_primary_drawer: false,
    show_product_image_in_drawer: false,
    // Quantity Entry Popup on cart add
    enable_qty_popup: false,
    // Allow editing of saved bills/estimations
    allow_bill_edit: false
  });

  const billTitleOptions = [
    { value: 'tax_invoice', label: 'Tax Invoice' },
    { value: 'receipt', label: 'Receipt' },
    { value: 'cash_bill', label: 'Cash Bill' },
    { value: 'tax_receipt', label: 'Tax Receipt' },
    { value: 'tax_bill', label: 'Tax Bill' },
    { value: 'custom', label: 'Custom Text' }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const response = await axios.get(`${API}/settings/bill`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormData(response.data);
    } catch (err) {
      setError('Failed to load bill settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('pos_token');
      await axios.put(`${API}/settings/bill`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Bill settings saved successfully!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="bill-settings-page" className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/gposp/settings')}
          className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          data-testid="back-button"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Bill Settings</h1>
          <p className="text-slate-500">Configure bill format and printing options</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
          {success}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Bill Format */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Bill Format</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bill Number Prefix (up to 50 chars)</label>
              <input
                type="text"
                name="bill_prefix"
                value={formData.bill_prefix}
                onChange={(e) => {
                  // Allow any characters (special chars included), max 50 chars
                  const val = e.target.value.replace(/[\r\n]/g, '').slice(0, 50);
                  setFormData({ ...formData, bill_prefix: val });
                }}
                maxLength="50"
                placeholder="e.g., INV, INV/24-25, BS#"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="bill-prefix-input"
              />
              <p className="mt-1 text-xs text-slate-500">
                Preview: {formData.bill_prefix || 'BS'}{formData.include_year_in_number === false ? '' : `-${String(new Date().getFullYear()).slice(-2)}-`}{String(formData.bill_start_number || 1).padStart(4, '0')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bill Starting Number</label>
              <input
                type="number"
                name="bill_start_number"
                value={formData.bill_start_number || 1}
                onChange={handleChange}
                min="1"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="bill-start-number-input"
              />
              <p className="mt-1 text-xs text-slate-500">Resets annually on Jan 1st</p>
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50">
                <input
                  type="checkbox"
                  name="include_year_in_number"
                  checked={formData.include_year_in_number !== false}
                  onChange={handleChange}
                  className="w-5 h-5 text-violet-600 rounded"
                  data-testid="include-year-toggle"
                />
                <div className="flex-1">
                  <p className="font-medium text-slate-800">Include current year (YY) in bill/estimation number</p>
                  <p className="text-sm text-slate-500">
                    On (default): <b>{formData.bill_prefix || 'BS'}-{String(new Date().getFullYear()).slice(-2)}-0001</b>. Off: <b>{formData.bill_prefix || 'BS'}0001</b> — put your own year/format inside the prefix (e.g. {'"GST 2026-2027-Apr-"'}).
                  </p>
                </div>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Estimation Prefix (up to 50 chars)</label>
              <input
                type="text"
                name="estimation_prefix"
                value={formData.estimation_prefix}
                onChange={(e) => {
                  const val = e.target.value.replace(/[\r\n]/g, '').slice(0, 50);
                  setFormData({ ...formData, estimation_prefix: val });
                }}
                maxLength="50"
                placeholder="e.g., EST, EST/24-25, ES#"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="estimation-prefix-input"
              />
              <p className="mt-1 text-xs text-slate-500">
                Preview: {formData.estimation_prefix || 'ES'}{formData.include_year_in_number === false ? '' : `-${String(new Date().getFullYear()).slice(-2)}-`}{String(formData.estimation_start_number || 1).padStart(4, '0')}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Estimation Starting Number</label>
              <input
                type="number"
                name="estimation_start_number"
                value={formData.estimation_start_number || 1}
                onChange={handleChange}
                min="1"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="estimation-start-number-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Paper Size</label>
              <select
                name="paper_size"
                value={formData.paper_size}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="paper-size-select"
              >
                <option value="58mm">58mm (Small Receipt)</option>
                <option value="80mm">80mm (Standard Receipt)</option>
                <option value="A4">A4 (Full Page)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Bill Title *</label>
              <select
                name="bill_title"
                value={formData.bill_title || 'tax_invoice'}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="bill-title-select"
              >
                {billTitleOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            {formData.bill_title === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Custom Bill Title</label>
                <input
                  type="text"
                  name="bill_title_custom"
                  value={formData.bill_title_custom || ''}
                  onChange={handleChange}
                  placeholder="Enter your custom title"
                  maxLength="30"
                  className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  data-testid="bill-title-custom-input"
                />
              </div>
            )}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">Tax Calculation Method *</label>
              <div className="grid grid-cols-2 gap-3">
                <label className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${formData.tax_method === 'inclusive' ? 'border-violet-600 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input
                    type="radio"
                    name="tax_method"
                    value="inclusive"
                    checked={formData.tax_method === 'inclusive' || !formData.tax_method}
                    onChange={handleChange}
                    className="w-4 h-4 text-violet-600"
                    data-testid="tax-inclusive-radio"
                  />
                  <div>
                    <p className="font-medium text-slate-800">Tax Inclusive</p>
                    <p className="text-xs text-slate-500">Tax is included in selling price (Default)</p>
                  </div>
                </label>
                <label className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${formData.tax_method === 'exclusive' ? 'border-violet-600 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <input
                    type="radio"
                    name="tax_method"
                    value="exclusive"
                    checked={formData.tax_method === 'exclusive'}
                    onChange={handleChange}
                    className="w-4 h-4 text-violet-600"
                    data-testid="tax-exclusive-radio"
                  />
                  <div>
                    <p className="font-medium text-slate-800">Tax Exclusive</p>
                    <p className="text-xs text-slate-500">Tax is added on top of selling price</p>
                  </div>
                </label>
              </div>
              <p className="mt-2 text-xs text-amber-600">Note: This affects how tax is calculated on bills</p>
            </div>
            
            {/* A4 Invoice Settings */}
            <div className="col-span-2 pt-4 border-t border-slate-200">
              <label className="block text-sm font-medium text-slate-700 mb-2">Invoice Print Format</label>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="enable_a4_invoice"
                    checked={formData.enable_a4_invoice}
                    onChange={handleChange}
                    className="w-5 h-5 text-violet-600 rounded"
                    data-testid="enable-a4-invoice-checkbox"
                  />
                  <div>
                    <p className="font-medium text-slate-800">Enable A4 GST Invoice PDF</p>
                    <p className="text-sm text-slate-500">Generate professional A4 format GST invoice with item discounts and tax breakup</p>
                  </div>
                </label>
                
                {formData.enable_a4_invoice && (
                  <div className="ml-8">
                    <label className="block text-sm font-medium text-slate-700 mb-2">Default Print Format</label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${formData.default_print_format === 'thermal' ? 'border-violet-600 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}>
                        <input
                          type="radio"
                          name="default_print_format"
                          value="thermal"
                          checked={formData.default_print_format === 'thermal' || !formData.default_print_format}
                          onChange={handleChange}
                          className="w-4 h-4 text-violet-600"
                        />
                        <div>
                          <p className="font-medium text-slate-800 text-sm">Thermal Receipt</p>
                          <p className="text-xs text-slate-500">58mm/80mm thermal print</p>
                        </div>
                      </label>
                      <label className={`flex items-center gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all ${formData.default_print_format === 'a4' ? 'border-violet-600 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}>
                        <input
                          type="radio"
                          name="default_print_format"
                          value="a4"
                          checked={formData.default_print_format === 'a4'}
                          onChange={handleChange}
                          className="w-4 h-4 text-violet-600"
                        />
                        <div>
                          <p className="font-medium text-slate-800 text-sm">A4 Invoice</p>
                          <p className="text-xs text-slate-500">Professional PDF invoice</p>
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* A4 Invoice Detail Settings */}
            {formData.enable_a4_invoice && (
              <div className="col-span-2 pt-4 border-t border-slate-200">
                <h3 className="text-sm font-semibold text-slate-700 mb-3">A4 Invoice - Header Details</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { name: 'a4_show_company_name', label: 'Company Name' },
                    { name: 'a4_show_address', label: 'Address' },
                    { name: 'a4_show_email', label: 'Email' },
                    { name: 'a4_show_phone', label: 'Phone Number' },
                    { name: 'a4_show_gst_number', label: 'GST Number' },
                  ].map(opt => (
                    <label key={opt.name} className="flex items-center gap-2 p-2.5 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        name={opt.name}
                        checked={formData[opt.name] !== false}
                        onChange={handleChange}
                        className="w-4 h-4 text-violet-600 rounded"
                        data-testid={`${opt.name}-toggle`}
                      />
                      <span className="text-sm text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>

                <h3 className="text-sm font-semibold text-slate-700 mb-3 mt-4">A4 Invoice - Content Options</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { name: 'a4_show_tax_summary', label: 'Tax Summary (GST)' },
                    { name: 'a4_show_mrp', label: 'MRP Column' },
                    { name: 'a4_show_product_image', label: 'Product Images' },
                  ].map(opt => (
                    <label key={opt.name} className="flex items-center gap-2 p-2.5 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        name={opt.name}
                        checked={formData[opt.name] === true}
                        onChange={handleChange}
                        className="w-4 h-4 text-violet-600 rounded"
                        data-testid={`${opt.name}-toggle`}
                      />
                      <span className="text-sm text-slate-700">{opt.label}</span>
                    </label>
                  ))}
                </div>

                <h3 className="text-sm font-semibold text-slate-700 mb-3 mt-4">Letterpad Mode (No Header)</h3>
                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      name="a4_no_header"
                      checked={formData.a4_no_header || false}
                      onChange={handleChange}
                      className="w-5 h-5 text-violet-600 rounded"
                      data-testid="a4-no-header-toggle"
                    />
                    <div>
                      <p className="font-medium text-slate-800 text-sm">Without Header (Letterpad Mode)</p>
                      <p className="text-xs text-slate-500">Hide company header — use when printing on pre-printed letterpad</p>
                    </div>
                  </label>
                  {formData.a4_no_header && (
                    <div className="ml-8">
                      <label className="block text-sm font-medium text-slate-700 mb-1">Top Margin (mm)</label>
                      <input
                        type="number"
                        name="a4_top_margin"
                        value={formData.a4_top_margin || 0}
                        onChange={handleChange}
                        min="0"
                        max="100"
                        placeholder="e.g., 30"
                        className="w-32 px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500"
                        data-testid="a4-top-margin-input"
                      />
                      <p className="text-xs text-slate-500 mt-1">Space from top of page before invoice content starts (in millimeters)</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Digital Signature for A4 Invoice */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Digital Signature</h2>
          <p className="text-sm text-slate-500 mb-4">Upload signature image for A4 Invoice (PNG/JPG, max 2MB)</p>
          <div className="space-y-3">
            {formData.signature_url ? (
              <div className="flex items-center gap-4">
                <div className="border border-slate-200 rounded-lg p-2 bg-slate-50">
                  <img src={formData.signature_url} alt="Signature" className="h-16 object-contain" data-testid="signature-preview" />
                </div>
                <div>
                  <p className="text-sm text-emerald-600 font-medium">Signature uploaded</p>
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm('Delete signature permanently?')) return;
                      try {
                        const token = localStorage.getItem('pos_token');
                        await axios.delete(`${API}/upload/signature`, { headers: { Authorization: `Bearer ${token}` } });
                        setFormData(prev => ({ ...prev, signature_url: null }));
                      } catch (err) { alert('Failed to delete'); }
                    }}
                    className="text-sm text-red-600 hover:text-red-800 mt-1"
                    data-testid="delete-signature-btn"
                  >
                    Delete Signature
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    if (file.size > 2 * 1024 * 1024) { alert('File must be less than 2MB'); return; }
                    if (!['image/png', 'image/jpeg'].includes(file.type)) { alert('Only PNG/JPG allowed'); return; }
                    const fd = new FormData();
                    fd.append('file', file);
                    try {
                      const token = localStorage.getItem('pos_token');
                      const resp = await axios.post(`${API}/upload/signature`, fd, {
                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
                      });
                      setFormData(prev => ({ ...prev, signature_url: resp.data.signature_url }));
                    } catch (err) { alert(err.response?.data?.detail || 'Upload failed'); }
                    e.target.value = '';
                  }}
                  className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
                  data-testid="signature-upload-input"
                />
                <p className="text-xs text-slate-400 mt-1">PNG or JPG, max 2MB. Will appear at the bottom of A4 invoices.</p>
              </div>
            )}
          </div>
        </div>

        {/* Previous Date Billing */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Previous Date Billing</h2>
          <p className="text-sm text-slate-500 mb-4">Allow creating bills and estimations with a past date</p>
          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="font-medium text-slate-800 text-sm">Allow Previous Date Bill/Estimation</p>
                <p className="text-xs text-slate-500">Show date picker in billing screen</p>
              </div>
              <input
                type="checkbox"
                name="allow_previous_date_bill"
                checked={formData.allow_previous_date_bill || false}
                onChange={handleChange}
                className="w-5 h-5 text-violet-600 rounded"
                data-testid="allow-previous-date-toggle"
              />
            </label>
            {formData.allow_previous_date_bill && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Permission</label>
                <div className="flex gap-3">
                  <label className={`flex items-center gap-2 p-3 border-2 rounded-xl cursor-pointer flex-1 ${formData.previous_date_permission === 'owner_only' ? 'border-violet-600 bg-violet-50' : 'border-slate-200'}`}>
                    <input type="radio" name="previous_date_permission" value="owner_only" checked={formData.previous_date_permission === 'owner_only' || !formData.previous_date_permission} onChange={handleChange} className="w-4 h-4 text-violet-600" />
                    <p className="font-medium text-slate-800 text-sm">Owner Only</p>
                  </label>
                  <label className={`flex items-center gap-2 p-3 border-2 rounded-xl cursor-pointer flex-1 ${formData.previous_date_permission === 'owner_and_cashiers' ? 'border-violet-600 bg-violet-50' : 'border-slate-200'}`}>
                    <input type="radio" name="previous_date_permission" value="owner_and_cashiers" checked={formData.previous_date_permission === 'owner_and_cashiers'} onChange={handleChange} className="w-4 h-4 text-violet-600" />
                    <p className="font-medium text-slate-800 text-sm">Owner & Cashiers</p>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* QR Code Settings */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">QR Code on Invoice</h2>
          <p className="text-sm text-slate-500 mb-4">Show QR code in the bill footer area</p>
          <div className="space-y-4">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <p className="font-medium text-slate-800 text-sm">Show QR Code</p>
                <p className="text-xs text-slate-500">Display QR code in A4 invoice footer</p>
              </div>
              <input
                type="checkbox"
                name="show_qr_code"
                checked={formData.show_qr_code || false}
                onChange={handleChange}
                className="w-5 h-5 text-violet-600 rounded"
                data-testid="show-qr-code-toggle"
              />
            </label>
            {formData.show_qr_code && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">QR Code Type</label>
                  <div className="flex gap-3">
                    <label className={`flex items-center gap-2 p-3 border-2 rounded-xl cursor-pointer flex-1 ${formData.qr_code_type === 'url' ? 'border-violet-600 bg-violet-50' : 'border-slate-200'}`}>
                      <input type="radio" name="qr_code_type" value="url" checked={formData.qr_code_type === 'url' || !formData.qr_code_type} onChange={handleChange} className="w-4 h-4 text-violet-600" />
                      <div>
                        <p className="font-medium text-slate-800 text-sm">URL</p>
                        <p className="text-xs text-slate-500">Static URL / Payment link</p>
                      </div>
                    </label>
                    <label className={`flex items-center gap-2 p-3 border-2 rounded-xl cursor-pointer flex-1 ${formData.qr_code_type === 'bill_details' ? 'border-violet-600 bg-violet-50' : 'border-slate-200'}`}>
                      <input type="radio" name="qr_code_type" value="bill_details" checked={formData.qr_code_type === 'bill_details'} onChange={handleChange} className="w-4 h-4 text-violet-600" />
                      <div>
                        <p className="font-medium text-slate-800 text-sm">Bill Details</p>
                        <p className="text-xs text-slate-500">Auto-generate with bill info</p>
                      </div>
                    </label>
                  </div>
                </div>
                {formData.qr_code_type === 'url' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">QR Code URL</label>
                    <input
                      type="url"
                      name="qr_code_url"
                      value={formData.qr_code_url || ''}
                      onChange={handleChange}
                      placeholder="https://example.com/pay or UPI link"
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                      data-testid="qr-code-url-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">This URL will be encoded as a QR code on every invoice</p>
                  </div>
                )}
                {formData.qr_code_type === 'bill_details' && (
                  <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                    QR code will auto-generate for each bill with: Bill Number, Date, Customer Name, Items, Total Amount, Tax Details — as readable plain text.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Billing Defaults */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Billing Defaults</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Default Bill Mode</label>
              <div className="flex gap-3">
                <label className={`flex items-center gap-2 p-3 border-2 rounded-xl cursor-pointer flex-1 ${formData.default_bill_mode === 'cash' ? 'border-violet-600 bg-violet-50' : 'border-slate-200'}`}>
                  <input type="radio" name="default_bill_mode" value="cash" checked={formData.default_bill_mode === 'cash' || !formData.default_bill_mode} onChange={handleChange} className="w-4 h-4 text-violet-600" data-testid="default-mode-cash" />
                  <span className="text-sm font-medium">Cash</span>
                </label>
                <label className={`flex items-center gap-2 p-3 border-2 rounded-xl cursor-pointer flex-1 ${formData.default_bill_mode === 'credit' ? 'border-violet-600 bg-violet-50' : 'border-slate-200'}`}>
                  <input type="radio" name="default_bill_mode" value="credit" checked={formData.default_bill_mode === 'credit'} onChange={handleChange} className="w-4 h-4 text-violet-600" data-testid="default-mode-credit" />
                  <span className="text-sm font-medium">Credit</span>
                </label>
              </div>
              <p className="text-xs text-slate-500 mt-1">Payment mode will be auto-selected during billing</p>
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="direct_print_on_payment"
                checked={formData.direct_print_on_payment}
                onChange={handleChange}
                className="w-5 h-5 text-violet-600 rounded"
                data-testid="direct-print-checkbox"
              />
              <div>
                <p className="font-medium text-slate-800">Direct Print on Payment</p>
                <p className="text-sm text-slate-500">Skip bill preview and print directly when payment is completed</p>
              </div>
            </label>
          </div>
        </div>

        {/* Quick Add & Estimation Settings */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Advanced Billing</h2>
          <p className="text-sm text-slate-500 mb-4">Custom items and estimation behavior</p>
          <div className="space-y-4">
            <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50">
              <input type="checkbox" name="allow_quick_add_items" checked={formData.allow_quick_add_items || false} onChange={handleChange} className="w-5 h-5 text-violet-600 rounded" data-testid="quick-add-toggle" />
              <div className="flex-1">
                <p className="font-medium text-slate-800">Allow Quick Add Items</p>
                <p className="text-sm text-slate-500">Add non-stock items with custom name & price during billing</p>
              </div>
            </label>
            {formData.allow_quick_add_items && (
              <div className="ml-8">
                <label className="block text-sm font-medium text-slate-700 mb-2">Who can add custom items?</label>
                <div className="flex gap-3">
                  <label className={`flex items-center gap-2 p-3 border-2 rounded-xl cursor-pointer flex-1 ${formData.quick_add_permission === 'owner_only' ? 'border-violet-600 bg-violet-50' : 'border-slate-200'}`}>
                    <input type="radio" name="quick_add_permission" value="owner_only" checked={formData.quick_add_permission === 'owner_only' || !formData.quick_add_permission} onChange={handleChange} className="w-4 h-4 text-violet-600" data-testid="quick-add-owner-only" />
                    <span className="text-sm font-medium">Owner Only</span>
                  </label>
                  <label className={`flex items-center gap-2 p-3 border-2 rounded-xl cursor-pointer flex-1 ${formData.quick_add_permission === 'owner_and_cashiers' ? 'border-violet-600 bg-violet-50' : 'border-slate-200'}`}>
                    <input type="radio" name="quick_add_permission" value="owner_and_cashiers" checked={formData.quick_add_permission === 'owner_and_cashiers'} onChange={handleChange} className="w-4 h-4 text-violet-600" data-testid="quick-add-all" />
                    <span className="text-sm font-medium">Owner & Cashiers</span>
                  </label>
                </div>
              </div>
            )}
            <div className="border-t border-slate-200 pt-4">
              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50">
                <input type="checkbox" name="estimation_with_delivery" checked={formData.estimation_with_delivery || false} onChange={handleChange} className="w-5 h-5 text-violet-600 rounded" data-testid="estimation-delivery-toggle" />
                <div className="flex-1">
                  <p className="font-medium text-slate-800">Estimation with Delivery</p>
                  <p className="text-sm text-slate-500">When enabled, estimations will deduct stock for delivery tracking purposes</p>
                </div>
              </label>
            </div>
            <div className="border-t border-slate-200 pt-4">
              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50">
                <input type="checkbox" name="show_primary_drawer" checked={formData.show_primary_drawer || false} onChange={handleChange} className="w-5 h-5 text-emerald-600 rounded" data-testid="primary-drawer-toggle" />
                <div className="flex-1">
                  <p className="font-medium text-slate-800">Show Primary Products Drawer</p>
                  <p className="text-sm text-slate-500">Cafe / Restaurant style — quick-pick drawer in POS Billing for products marked as Primary. Tap a card to add to cart.</p>
                </div>
              </label>
              {formData.show_primary_drawer && (
                <label className="ml-8 mt-3 flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" name="show_product_image_in_drawer" checked={formData.show_product_image_in_drawer || false} onChange={handleChange} className="w-5 h-5 text-violet-600 rounded" data-testid="drawer-images-toggle" />
                  <div className="flex-1">
                    <p className="font-medium text-slate-800">Show Product Images in Drawer <span className="text-xs text-violet-600 font-semibold ml-1">PREMIUM</span></p>
                    <p className="text-sm text-slate-500">Display the uploaded product image inside drawer cards. Available on paid plans only — falls back gracefully if not eligible.</p>
                  </div>
                </label>
              )}
            </div>
            <div className="border-t border-slate-200 pt-4">
              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50">
                <input type="checkbox" name="enable_qty_popup" checked={formData.enable_qty_popup || false} onChange={handleChange} className="w-5 h-5 text-violet-600 rounded" data-testid="enable-qty-popup-toggle" />
                <div className="flex-1">
                  <p className="font-medium text-slate-800">Enable Quantity Entry Popup</p>
                  <p className="text-sm text-slate-500">When a product is selected during billing, show a popup to enter Qty, Price and Discount before adding to cart. Off = add with default qty 1.</p>
                </div>
              </label>
            </div>
            <div className="border-t border-slate-200 pt-4">
              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50">
                <input type="checkbox" name="allow_bill_edit" checked={formData.allow_bill_edit || false} onChange={handleChange} className="w-5 h-5 text-amber-600 rounded" data-testid="allow-bill-edit-toggle" />
                <div className="flex-1">
                  <p className="font-medium text-slate-800">Allow Bill Editing After Save</p>
                  <p className="text-sm text-slate-500">Permit editing saved bills/estimations. Stock and ledger entries are auto-rebalanced. An edit history audit trail is kept. Off = saved bills are read-only.</p>
                </div>
              </label>
            </div>
            <div className="border-t border-slate-200 pt-4">
              <label className="flex items-center gap-3 p-3 border border-amber-200 rounded-xl cursor-pointer hover:bg-amber-50">
                <input type="checkbox" name="enable_dual_unit_stock" checked={formData.enable_dual_unit_stock || false} onChange={handleChange} className="w-5 h-5 text-amber-600 rounded" data-testid="enable-dual-unit-toggle" />
                <div className="flex-1">
                  <p className="font-medium text-slate-800">Enable Dual-Unit Stock (buy/stock vs sell)</p>
                  <p className="text-sm text-slate-500">For shops that buy &amp; stock in one unit (e.g. pcs) but sell &amp; price in another (e.g. kg). When on, each product can be set to "track stock separately", and billing asks for the pieces to deduct from stock. Off = normal single-unit behaviour.</p>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Display Options */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Display Options</h2>
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="print_logo"
                checked={formData.print_logo}
                onChange={handleChange}
                className="w-5 h-5 text-violet-600 rounded"
                data-testid="print-logo-checkbox"
              />
              <div>
                <p className="font-medium text-slate-800">Print Business Logo</p>
                <p className="text-sm text-slate-500">Show your business logo at the top of bills</p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="show_gst_breakdown"
                checked={formData.show_gst_breakdown}
                onChange={handleChange}
                className="w-5 h-5 text-violet-600 rounded"
                data-testid="show-gst-checkbox"
              />
              <div>
                <p className="font-medium text-slate-800">Show GST Breakdown</p>
                <p className="text-sm text-slate-500">Display CGST and SGST amounts separately</p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="show_mrp"
                checked={formData.show_mrp !== false}
                onChange={handleChange}
                className="w-5 h-5 text-violet-600 rounded"
                data-testid="show-mrp-checkbox"
              />
              <div>
                <p className="font-medium text-slate-800">Show MRP on Bills</p>
                <p className="text-sm text-slate-500">Display MRP next to item description on Thermal & A4 prints</p>
              </div>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="show_savings"
                checked={formData.show_savings}
                onChange={handleChange}
                className="w-5 h-5 text-violet-600 rounded"
                data-testid="show-savings-checkbox"
              />
              <div>
                <p className="font-medium text-slate-800">Show Savings</p>
                <p className="text-sm text-slate-500">Display total savings when discounts are applied</p>
              </div>
            </label>
          </div>
        </div>

        {/* Price Editing Permission */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Billing Permissions</h2>
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="allow_price_edit"
                checked={formData.allow_price_edit}
                onChange={handleChange}
                className="w-5 h-5 text-violet-600 rounded"
                data-testid="allow-price-edit-checkbox"
              />
              <div>
                <p className="font-medium text-slate-800">Allow Price Edit During Billing</p>
                <p className="text-sm text-slate-500">Enable editing of selling price while creating bills</p>
              </div>
            </label>

            {formData.allow_price_edit && (
              <div className="ml-8 p-4 bg-slate-50 rounded-lg">
                <label className="block text-sm font-medium text-slate-700 mb-2">Who can edit prices?</label>
                <div className="space-y-2">
                  <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${formData.price_edit_permission === 'owner_only' ? 'border-violet-600 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <input
                      type="radio"
                      name="price_edit_permission"
                      value="owner_only"
                      checked={formData.price_edit_permission === 'owner_only'}
                      onChange={handleChange}
                      className="w-4 h-4 text-violet-600"
                      data-testid="price-edit-owner-only-radio"
                    />
                    <div>
                      <p className="font-medium text-slate-800">Owner Only</p>
                      <p className="text-xs text-slate-500">Only the business owner can edit prices</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all ${formData.price_edit_permission === 'owner_and_cashiers' ? 'border-violet-600 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <input
                      type="radio"
                      name="price_edit_permission"
                      value="owner_and_cashiers"
                      checked={formData.price_edit_permission === 'owner_and_cashiers'}
                      onChange={handleChange}
                      className="w-4 h-4 text-violet-600"
                      data-testid="price-edit-owner-cashiers-radio"
                    />
                    <div>
                      <p className="font-medium text-slate-800">Owner & Cashiers</p>
                      <p className="text-xs text-slate-500">Both owner and cashiers can edit prices</p>
                    </div>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Printer Hardware Settings */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-1">Printer Hardware</h2>
          <p className="text-sm text-slate-500 mb-4">Configure thermal printer connection</p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Printer Type</label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { value: 'browser', label: 'Browser Print', desc: 'Default browser print dialog' },
                  { value: 'bluetooth', label: 'Bluetooth', desc: 'Bluetooth thermal printer' },
                  { value: 'usb', label: 'USB', desc: 'USB connected printer' }
                ].map(opt => (
                  <label key={opt.value} className={`flex flex-col p-3 border-2 rounded-xl cursor-pointer transition-all ${formData.printer_type === opt.value ? 'border-violet-600 bg-violet-50' : 'border-slate-200 hover:border-slate-300'}`}>
                    <div className="flex items-center gap-2">
                      <input type="radio" name="printer_type" value={opt.value} checked={formData.printer_type === opt.value} onChange={handleChange} className="w-4 h-4 text-violet-600" data-testid={`printer-${opt.value}`} />
                      <span className="font-medium text-slate-800 text-sm">{opt.label}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-6">{opt.desc}</p>
                  </label>
                ))}
              </div>
            </div>
            {formData.printer_type === 'bluetooth' && (
              <div className="ml-4 p-4 bg-slate-50 rounded-lg space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bluetooth Device Name</label>
                  <input type="text" name="bluetooth_device_name" value={formData.bluetooth_device_name || ''} onChange={handleChange}
                    placeholder="e.g., POS-58 Printer" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500" data-testid="bluetooth-device-input" />
                  <p className="text-xs text-slate-500 mt-1">Enter the name of your Bluetooth thermal printer. Make sure it's paired with your device.</p>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                  Web Bluetooth requires Chrome/Edge on HTTPS. Pair your printer via device settings first, then enter the device name here.
                </div>
              </div>
            )}
            {formData.printer_type === 'usb' && (
              <div className="ml-4 p-4 bg-slate-50 rounded-lg space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">USB Printer Path</label>
                  <input type="text" name="usb_printer_path" value={formData.usb_printer_path || ''} onChange={handleChange}
                    placeholder="e.g., /dev/usb/lp0" className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500" data-testid="usb-path-input" />
                  <p className="text-xs text-slate-500 mt-1">Web USB API will attempt to connect to the printer. Chrome/Edge on HTTPS required.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer & Terms */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Footer & Terms</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Footer Text</label>
              <input
                type="text"
                name="footer_text"
                value={formData.footer_text}
                onChange={handleChange}
                placeholder="e.g., Thank you for shopping with us!"
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="footer-text-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Terms & Conditions</label>
              <textarea
                name="terms_text"
                value={formData.terms_text}
                onChange={handleChange}
                rows={3}
                placeholder="Enter your return/exchange policy..."
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                data-testid="terms-text-input"
              />
            </div>
          </div>
        </div>

        {/* Bill Preview */}
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h2 className="text-lg font-semibold text-slate-800 mb-4">Preview</h2>
          <div className="max-w-xs mx-auto bg-slate-50 border border-slate-200 rounded-lg p-4 font-mono text-xs">
            {formData.print_logo && (
              <div className="text-center mb-2 text-slate-400">[Business Logo]</div>
            )}
            <div className="text-center font-bold mb-2">BUSINESS NAME</div>
            <div className="text-center text-slate-500 mb-3">Address | Phone</div>
            <div className="border-t border-dashed border-slate-300 my-2"></div>
            <div className="text-slate-600 mb-1">Bill: {formData.bill_prefix || 'STORE'}-20241208...</div>
            <div className="text-slate-600 mb-2">Date: 08/12/2024 12:00 PM</div>
            <div className="border-t border-dashed border-slate-300 my-2"></div>
            <div className="space-y-1 mb-2">
              <div className="flex justify-between">
                <span>Item 1 x2</span>
                <span>200.00</span>
              </div>
              <div className="flex justify-between">
                <span>Item 2 x1</span>
                <span>150.00</span>
              </div>
            </div>
            <div className="border-t border-dashed border-slate-300 my-2"></div>
            <div className="flex justify-between">
              <span>Subtotal</span>
              <span>350.00</span>
            </div>
            {formData.show_gst_breakdown && (
              <>
                <div className="flex justify-between text-slate-500">
                  <span>CGST (9%)</span>
                  <span>31.50</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>SGST (9%)</span>
                  <span>31.50</span>
                </div>
              </>
            )}
            {formData.show_savings && (
              <div className="flex justify-between text-green-600">
                <span>You Saved</span>
                <span>50.00</span>
              </div>
            )}
            <div className="border-t border-slate-300 my-2"></div>
            <div className="flex justify-between font-bold">
              <span>TOTAL</span>
              <span>413.00</span>
            </div>
            <div className="border-t border-dashed border-slate-300 my-2"></div>
            {formData.footer_text && (
              <div className="text-center text-slate-600 mb-1">{formData.footer_text}</div>
            )}
            {formData.terms_text && (
              <div className="text-center text-slate-400 text-[10px]">{formData.terms_text}</div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-8 py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold rounded-xl hover:from-violet-700 hover:to-purple-700 transition-all disabled:opacity-50"
            data-testid="save-button"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BillSettings;
